import { Typography } from '@mui/material';
import React from 'react';
import StyBoxAdmin from 'routes/Admin/shared/StyBoxAdmin';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import Loading from 'components/Loading';
import {
    useCadrageExerciceSelection,
    useCadrageMetricSelection,
    useUpdateCadrageConfig,
} from './hook';
import {
    trackEventNewCadrageExerciceSelected,
    trackEventNewCadrageMetricSelected,
} from './helper';
import { useTranslation } from 'react-i18next';
import CustomCadrageBpsIndicator from './components/CustomCadrageBpsIndicator';
import BudgetAutocomplete from 'routes/BudgetModule/components/shared/BudgetAutocomplete/BudgetAutocomplete';
import { AvailableCadrageMetricsT } from './type';
import { StyCadrageFilters } from './style';
import CadrageTitle from './components/CadrageTitle';
import CustomCadrageGrid from './CustomCadrage/CustomCadrageGrid';

const ManageCadrage = ({
    budgetProposals = [],
    isLoading = false,
}: {
    budgetProposals?: BudgetProposalWithResourcesT[];
    isLoading?: boolean;
}): React.ReactElement => {
    const { t } = useTranslation();

    const { exercice, setExercice, sortedExercices } =
        useCadrageExerciceSelection(budgetProposals);
    const { metric, availableMetrics } = useCadrageMetricSelection(exercice);

    const { mutate: updateCadrageConfig } = useUpdateCadrageConfig();
    const setSelectedMetric = (newMetric: AvailableCadrageMetricsT): void => {
        if (metric?.key === newMetric.key || exercice == null) return;
        updateCadrageConfig({
            exercice: exercice,
            metric: newMetric.metric,
            yearOffset: newMetric.yearOffset,
        });
        trackEventNewCadrageMetricSelected(newMetric.metric);
    };
    const setSelectedExercice = (newValue: number): void => {
        setExercice(newValue);
        trackEventNewCadrageExerciceSelected(newValue);
    };

    if (isLoading || !exercice) {
        return <Loading />;
    }
    return (
        <StyBoxAdmin sx={{ m: '1rem', height: 'auto' }}>
            <StyCadrageFilters>
                <Typography variant="h5" sx={{ fontWeight: 'normal' }}>
                    {t('adminBudgetSpace.cadrage.exercice')}
                </Typography>
                <BudgetAutocomplete
                    disableClearable
                    value={exercice}
                    onChange={setSelectedExercice}
                    options={sortedExercices}
                />
            </StyCadrageFilters>
            <CadrageTitle text={t('adminBudgetSpace.cadrage.metricTitle')} />
            <StyCadrageFilters>
                <Typography variant="h5" sx={{ fontWeight: 'normal' }}>
                    {t('adminBudgetSpace.cadrage.metric')}
                </Typography>
                <BudgetAutocomplete
                    disableClearable
                    value={metric}
                    onChange={setSelectedMetric}
                    options={availableMetrics}
                    getOptionLabel={(option) => option.key}
                    inputWidth="10rem"
                />
                <CustomCadrageBpsIndicator exercice={exercice} />
            </StyCadrageFilters>
            <CadrageTitle text={t('adminBudgetSpace.cadrage.customTitle')} />
            <CustomCadrageGrid
                exercice={exercice}
                budgetProposals={budgetProposals}
            />
        </StyBoxAdmin>
    );
};

export default ManageCadrage;
